import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CardShell from '../../components/CardShell';
import { useP } from '../../i18n/useP';
import { OfferIcon, iconOf } from './offerIcons';
import './Sales.css';

/* 12 MOIS GLISSANTS (refait le 2026-10-10 au format des autres pages, proto
   `WIP/protos/facturation-homogene.html`) : chaque colonne est le cumul des
   douze mois qui la finissent, les douze colonnes finissant au dernier mois
   qui porte une facture. Rangée par offre comme le Réalisé ; une catégorie se
   retrouve d'une année à l'autre par son LIBELLÉ (les identifiants changent
   d'un exercice à l'autre). */
function formatCHF(value, unit = 'kchf') {
  if (value === null || value === undefined || Math.abs(value) < (unit === 'kchf' ? 500 : 0.5)) return '–';
  return Math.round(unit === 'kchf' ? value / 1000 : value).toLocaleString('de-CH');
}

export default function Billing12MTab({ dataYear, dataPrevYear, dataPrev2Year, year, offers = [], filtreEntete = null, unit = 'kchf', kind = 'billing' }) {
  const { t } = useTranslation();
  const p = useP();
  const MONTHS = useMemo(() => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)), [t]);
  const parAn = useMemo(() => ({ [year]: dataYear || [], [year - 1]: dataPrevYear || [], [year - 2]: dataPrev2Year || [] }),
    [year, dataYear, dataPrevYear, dataPrev2Year]);

  /* Les douze colonnes, finissant au dernier mois facturé. */
  const colonnes = useMemo(() => {
    let fin = null;
    [year, year - 1, year - 2].forEach((yr) => {
      if (fin) return;
      for (let m = 11; m >= 0; m -= 1) {
        if ((parAn[yr] || []).some((r) => (r.monthly_values?.[m] || 0) !== 0)) { fin = { yr, m }; return; }
      }
    });
    if (!fin) fin = { yr: year, m: 0 };
    const out = [];
    for (let k = 11; k >= 0; k -= 1) {
      let m = fin.m - k; let yr = fin.yr;
      while (m < 0) { m += 12; yr -= 1; }
      out.push({ yr, m, label: `${MONTHS[m]} ${String(yr).slice(2)}` });
    }
    return out;
  }, [parAn, year, MONTHS]);

  const glissant = (label, col) => {
    let s = 0;
    for (let k = 0; k < 12; k += 1) {
      let m = col.m - k; let yr = col.yr;
      while (m < 0) { m += 12; yr -= 1; }
      const r = (parAn[yr] || []).find((x) => x.label === label);
      s += r ? (Number(r.monthly_values?.[m]) || 0) : 0;
    }
    return s;
  };

  const offreDe = (id) => offers.find((o) => o.id === id) || null;
  const groupes = [];
  const rang = (r) => (r.offer_id && offers.findIndex((o) => o.id === r.offer_id) >= 0 ? offers.findIndex((o) => o.id === r.offer_id) : 9999);
  [...(dataYear || [])].sort((a, b) => (rang(a) - rang(b)) || (a.label || '').localeCompare(b.label || '')).forEach((r) => {
    const cle = r.offer_id && offreDe(r.offer_id) ? r.offer_id : '';
    const g = groupes[groupes.length - 1];
    if (g && g.cle === cle) g.rangs.push(r); else groupes.push({ cle, offre: cle ? offreDe(cle) : null, rangs: [r] });
  });

  const total = colonnes.map((c) => (dataYear || []).reduce((a, r) => a + glissant(r.label, c), 0));
  return (
    <CardShell title={kind === 'ca' ? p("Chiffre d'affaires 12 mois glissants") : p('Facturation 12 mois glissants')} flush primaryAction={filtreEntete}>
      {!(dataYear || []).length
        ? <div className="sales-empty">{t('scenarii:billing.rolling12m.empty')}</div>
        : (
          <div className="sales-bill-scroll lk-hscroll">
            <table className="lk-entry-table sales-bill-table sb-table">
              <colgroup><col style={{ width: 300 }} />{colonnes.map((c) => <col key={c.label} style={{ width: 76 }} />)}</colgroup>
              <thead>
                <tr>
                  <th className="sb-lbl">{p('Offre · catégorie')}</th>
                  {colonnes.map((c, i) => <th key={c.label} className={i === 0 ? 'lk-sep' : ''}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {groupes.map((g) => (
                  <React.Fragment key={g.cle || 'libres'}>
                    <tr className="sb-offre">
                      <td className="sb-lbl">
                        {g.offre && <OfferIcon name={iconOf(g.offre)} size={15} />}
                        {g.offre ? (g.offre.label || g.offre.name) : t('scenarii:billing.actual.noOffer', 'Non rattaché à une offre')}
                      </td>
                      {colonnes.map((c, i) => (
                        <td key={c.label} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>
                          {formatCHF(g.rangs.reduce((a, r) => a + glissant(r.label, c), 0), unit)}
                        </td>
                      ))}
                    </tr>
                    {g.rangs.map((r) => (
                      <tr key={r.id} className="sb-cat">
                        <td className="sb-lbl sb-lbl--lu">{r.label}</td>
                        {colonnes.map((c, i) => <td key={c.label} className={`lk-num${i === 0 ? ' lk-sep' : ''}`}>{formatCHF(glissant(r.label, c), unit)}</td>)}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="sb-total-row">
                  <td className="sb-lbl">{t('scenarii:billing.rolling12m.total12m')}</td>
                  {total.map((v, i) => <td key={colonnes[i].label} className={i === 0 ? 'lk-sep' : ''}>{formatCHF(v, unit)}</td>)}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
    </CardShell>
  );
}
